import type React from 'react'
import styled, { css } from 'styled-components'

import { FormControlLabel } from '@components/ui'
import { Checkbox } from '@components/ui/Checkbox'
import { List, ListItem } from '@components/ui/List'

import type { CheckboxListProps, PaidStatusFilterInnerKeys, PaidStatusFilterKeys } from '../types'

function getListStyles(variant: 'form' | 'list' | undefined) {
  switch (variant) {
    case 'form':
      return css`
        display: flex;
        width: 100%;
      `
    case 'list':
      return css`
        max-height: 300px;
        overflow: auto;
      `
    default:
      return
  }
}

const StyledList = styled(List)<{ $variant?: 'form' | 'list' }>`
  ${({ $variant }) => getListStyles($variant)}

  li {
    padding: 5px 0;
    display: block;
  }

  & & {
    padding-left: 30px;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)`
  & > span:last-child {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.gray[100]};
  }
`

/**
 * Component to display checkboxes based on fields config
 *
 * @param {CheckboxListProps} {
 *  fields,
 *  onChange,
 *  value
 *  variant
 * }
 */
export function CheckboxList({ fields, onChange, value, variant }: CheckboxListProps) {
  function handleChange(innerName?: PaidStatusFilterInnerKeys, newValues?: string[]) {
    return function handler(event: React.ChangeEvent<HTMLInputElement>) {
      if (innerName) {
        onChange({
          ...value,
          [event.target.name]: event.target.checked || event.target.indeterminate,
          [innerName]: event.target.checked ? newValues : [],
        })
      } else {
        onChange({ ...value, [event.target.name]: event.target.checked })
      }
    }
  }

  function handleInnerChange(name: PaidStatusFilterKeys, innerName: PaidStatusFilterInnerKeys) {
    const values = value[innerName]
    return function handler(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.checked) {
        // add to selection
        onChange({
          ...value,
          [name]: true,
          [innerName]: [...values, event.target.name],
        })
      } else {
        // remove from selection
        const newValues = values.filter(innerValue => innerValue !== event.target.name)
        onChange({
          ...value,
          [name]: newValues.length > 0,
          [innerName]: newValues,
        })
      }
    }
  }
  return (
    <StyledList dense disablePadding $variant={variant}>
      {fields.map(listItem => (
        <ListItem key={listItem.name}>
          <StyledFormControlLabel
            checked={
              value[listItem.name] &&
              (listItem.inner ? value[listItem.inner.name].length === listItem.inner.options.length : true)
            }
            control={
              <Checkbox
                indeterminate={Boolean(
                  listItem.inner &&
                    value[listItem.inner.name].length > 0 &&
                    value[listItem.inner.name].length < listItem.inner.options.length
                )}
              />
            }
            gap={10}
            label={listItem.label}
            name={listItem.name}
            onChange={handleChange(
              listItem.inner?.name,
              listItem.inner?.options.map(o => o.name)
            )}
          />
          {listItem.inner && (
            <StyledList dense disablePadding>
              {listItem.inner.options.map(
                innerListItem =>
                  listItem.inner && (
                    <ListItem key={`${listItem.inner.name}-${innerListItem.name}`}>
                      <StyledFormControlLabel
                        checked={value[listItem.inner.name].includes(innerListItem.name)}
                        control={<Checkbox />}
                        gap={10}
                        label={innerListItem.label}
                        name={innerListItem.name}
                        onChange={handleInnerChange(listItem.name, listItem.inner.name)}
                      />
                    </ListItem>
                  )
              )}
            </StyledList>
          )}
        </ListItem>
      ))}
    </StyledList>
  )
}
